import { useFerryCredentials } from '@my/ferry-api/hooks';
import { useOrderResources } from '@my/tms-api';
import { cn } from '@my/ui';
import { Check, Ship, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IntegrationSwitch } from '../../ferry/components/IntegrationSwitch';
import { useShippingIntegration } from '../../ferry/shippingIntegration';
import { useCrossingCatalogue } from '../../ferry/useCrossingCatalogue';
import { bookingTypeShape } from '../../orders/lib/bookingForm';
import { buildBookingDetail, buildJourney, type SailingRow } from '../lib/bookingDetail';
import type { BookingRow } from '../lib/mockData';
import { useCockpitBooking } from '../lib/useCockpitBooking';
import { Dot, DocInput, JourneyTimeline, KV, ReqChip, Section } from './bookingPanelBits';
import { SailingOptions } from './SailingOptions';
import { SailingTypePill } from './SailingTypePill';

/**
 * Expand-down ferry booking screen (spec §6 individual booking modal), rendered
 * inline under a double-clicked To Book row. Three columns — unit / movement /
 * customs (left), the sailing picker (centre) and the journey timeline (right)
 * — over a dark header and a Book/Cancel footer. The sailings are the ferry
 * service's, per candidate crossing (`SailingOptions`); the unit is the order's.
 */
export function BookingExpand({
  row,
  orgId,
  onClose,
}: {
  row: BookingRow;
  /** Owning org — needed to resolve port addresses + create the booking. */
  orgId: string | null;
  onClose: () => void;
}) {
  const detail = useMemo(() => buildBookingDetail(row), [row]);
  const [selected, setSelected] = useState<SailingRow | null>(null);
  const deselect = useCallback(() => setSelected(null), []);
  // The switch: null until the operator clicks it.
  const [ferryBook, setFerryBook] = useState<boolean | null>(null);
  const { enabled: integrationEnabled } = useShippingIntegration(orgId);
  const credentialsQuery = useFerryCredentials(integrationEnabled ? orgId : null);
  const [credentialId, setCredentialId] = useState('');
  const [exportId, setExportId] = useState('');
  const [importId, setImportId] = useState('');

  const { routeByKey, carrierByKey } = useCrossingCatalogue();

  // The switch can be On only while a crossing on the table is at a carrier
  // the organisation holds an active account with; it starts On then, and is
  // Off and grey otherwise.
  const canIntegrate = useMemo(() => {
    const codes = new Set(
      (credentialsQuery.data ?? []).flatMap((c) =>
        c.status === 'Active' && c.carrierCode ? [c.carrierCode.toUpperCase()] : [],
      ),
    );
    if (!integrationEnabled) return false;
    return detail.candidates.some((leg) => {
      const entry = routeByKey.get(leg.candidate.catalogueKey);
      const code = entry ? carrierByKey.get(entry.carrierKey)?.ferryCarrierCode : null;
      return !!code && codes.has(code.toUpperCase());
    });
  }, [integrationEnabled, credentialsQuery.data, detail.candidates, routeByKey, carrierByKey]);
  const integrationOn = canIntegrate && (ferryBook ?? true);

  // The unit as the order has it: the assigned vehicle and trailer, and the
  // booking type the task screen defaults to for the cargo.
  const resourcesQuery = useOrderResources(row.id);
  const plate = (slot: 'Vehicle' | 'Trailer'): string => {
    const r = (resourcesQuery.data ?? []).find((x) => x.slot === slot);
    return r?.referenceSnapshot ?? r?.externalReference ?? '';
  };
  const vehicleRegistration = plate('Vehicle');
  const trailerNumber = plate('Trailer');
  const bookingType = row.containerCargo ? 'Container40to49' : 'Artic';
  const isAccompanied = !bookingTypeShape(bookingType).forceUnaccompanied;

  // Timeline / footer / latest-at-port all follow the selected sailing.
  const journey = useMemo(() => buildJourney(selected, detail.ctx), [selected, detail.ctx]);

  const { bookRow, isBooking } = useCockpitBooking(orgId, row.id);
  const canBook = !!selected && selected.viable && !journey.warning && !isBooking;

  const handleBook = async () => {
    if (!selected) return;
    const ok = await bookRow(row, selected, {
      bookingType,
      isAccompanied,
      numberOfDrivers: isAccompanied ? '1' : '',
      vehicleRegistration,
      trailerNumber,
      ferryBook: integrationOn,
      credentialId,
      exportId,
      importId,
    });
    if (ok) onClose();
  };

  // A change of switch, by a click or by the credential check settling, changes
  // what each crossing offers; the pick starts over.
  useEffect(() => setSelected(null), [integrationOn]);
  const toggleFerry = () => setFerryBook(!integrationOn);

  // The carrier's own figures for the derived vehicle type, once a sailing is picked.
  const vehicleType = selected?.ferry?.vehicleType ?? null;
  const dims =
    vehicleType &&
    vehicleType.defaultLength != null &&
    vehicleType.defaultWidth != null &&
    vehicleType.defaultHeight != null
      ? `${vehicleType.defaultLength} × ${vehicleType.defaultWidth} × ${vehicleType.defaultHeight} m`
      : '—';
  const weight =
    vehicleType?.defaultWeight != null ? `${(vehicleType.defaultWeight / 1000).toFixed(1)} t` : '—';
  const credentials = selected?.ferry?.credentialRequired ? selected.ferry.credentials : [];

  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 bg-slate-800 px-3 py-2 text-xs text-slate-200">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-emerald-500/90 text-white">
          <Ship className="h-3 w-3" aria-hidden="true" />
        </span>
        <span className="font-semibold text-white">Ferry Booking</span>
        <span className="font-semibold text-emerald-400">{row.orderNumber}</span>
        <Dot />
        <span className="text-slate-100">{row.customer}</span>
        <Dot />
        <span>{row.corridor}</span>
        <Dot />
        <span>{detail.routeLabel}</span>
        <Dot />
        <span>
          Deliver {detail.ctx.deliveryDateLabel} · {row.window}
        </span>
        {detail.customs && (
          <span className="rounded bg-amber-400/90 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-900">
            Customs
          </span>
        )}

        {integrationEnabled && (
          <div className="ml-auto flex items-center gap-3">
            <IntegrationSwitch
              dark
              on={integrationOn}
              onToggle={toggleFerry}
              disabled={isBooking || !canIntegrate}
            />
          </div>
        )}
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-[15rem_1fr_17rem]">
        {/* Left — unit / movement / requirements / customs / collection */}
        <aside className="space-y-4 border-b border-slate-200 p-3 text-xs lg:border-b-0 lg:border-r">
          <Section title="Unit">
            <KV k="Vehicle" v={vehicleRegistration || '—'} />
            <KV k="Trailer" v={trailerNumber || '—'} />
            <KV k="Dimensions" v={dims} />
            <KV k="Weight" v={weight} />
          </Section>

          <Section title="Movement">
            <KV k="Accompanied" v={isAccompanied ? 'Yes · 1 driver' : 'No'} />
            <KV k="Contact" v={detail.movement.contact} />
            <KV k="Haulier type" v={detail.movement.haulier} />
            {credentials.length > 0 && (
              <div className="flex items-center justify-between gap-2 py-0.5">
                <span className="text-[11px] text-slate-400">Account</span>
                <select
                  value={credentialId}
                  onChange={(e) => setCredentialId(e.target.value)}
                  aria-label="Carrier account"
                  className="w-28 rounded-sm border border-slate-300 bg-white px-1 py-0.5 text-[11px] text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  <option value="">Pick…</option>
                  {credentials.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.customerCode}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </Section>

          <Section title="Booking requirements">
            <div className="space-y-1">
              {detail.requirements.map((r) => (
                <ReqChip key={r.label} req={r} />
              ))}
            </div>
          </Section>

          {detail.customsDocs && (
            <Section title="Customs documents">
              <p className="mb-1.5 text-[11px] text-slate-500">{detail.customsDocs.note}</p>
              <DocInput
                label="PBN"
                side="Exit"
                placeholder="Enter PBN reference"
                value={exportId}
                onChange={setExportId}
              />
              <DocInput
                label="GMR"
                side="Entry"
                placeholder="Enter GMR reference"
                value={importId}
                onChange={setImportId}
              />
              <p className="mt-1.5 text-[11px] font-medium text-red-600">
                All customs references required before booking
              </p>
            </Section>
          )}

          <Section title="Collection to port">
            <KV k="Road time" v={detail.collection.roadTime} />
            <KV k="Latest at port" v={journey.latestAtPort} amber />
            <KV k="Delivery window" v={row.window} red />
          </Section>
        </aside>

        {/* Centre — sailing picker */}
        <div className="min-w-0 overflow-x-auto">
          <table className="w-full table-auto text-xs">
            <thead className="border-b border-slate-200 text-[10px] uppercase tracking-wide text-slate-400">
              <tr>
                <th className="w-8 px-2 py-2" />
                <th className="px-2 py-2 text-left font-semibold">Route · road to dest</th>
                <th className="px-2 py-2 text-left font-semibold">Operator</th>
                <th className="px-2 py-2 text-left font-semibold">Departs</th>
                <th className="px-2 py-2 text-left font-semibold">Arrives</th>
                <th className="px-2 py-2 text-left font-semibold">Duration</th>
                <th className="px-2 py-2 text-left font-semibold">Type</th>
                <th className="px-2 py-2 text-left font-semibold">Block</th>
                <th className="px-2 py-2 text-left font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {detail.candidates.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-2 py-4 text-center text-[11px] italic text-slate-400">
                    {detail.ctx.noFitReason ?? 'No route on file for this crossing'}
                  </td>
                </tr>
              ) : (
                detail.candidates.map((leg, i) => {
                  const entry = routeByKey.get(leg.candidate.catalogueKey);
                  if (!entry) return null;
                  return (
                    <SailingOptions
                      key={leg.key}
                      orgId={orgId}
                      row={row}
                      leg={leg}
                      entry={entry}
                      carrier={carrierByKey.get(entry.carrierKey) ?? null}
                      ctx={detail.ctx}
                      bookingType={bookingType}
                      accompanied={isAccompanied}
                      integrationOn={integrationOn}
                      selectedId={selected?.id ?? null}
                      onSelect={setSelected}
                      onDeselect={deselect}
                      index={i}
                    />
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Right — journey timeline */}
        <JourneyTimeline
          routeLabel={detail.routeLabel}
          warning={journey.warning}
          steps={journey.steps}
        />
      </div>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-200 bg-slate-50 px-3 py-2 text-xs">
        <span className="text-slate-500">
          Selected{' '}
          {selected ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="font-semibold tabular-nums text-slate-800">
                {selected.departs} to {selected.arrives}
              </span>
              <SailingTypePill type={selected.type} />
            </span>
          ) : (
            <span className="italic text-slate-400">none</span>
          )}
        </span>
        <Dot dark />
        <span className="text-slate-500">
          Road{' '}
          <span className="font-medium text-slate-700">
            {journey.footer.roadKm}km · {journey.footer.driveMin} min to {journey.footer.dest}
          </span>
        </span>
        {journey.footer.plugLabel && (
          <>
            <Dot dark />
            <span className="text-slate-400">{journey.footer.plugLabel}</span>
          </>
        )}

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            aria-label="Cancel"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label="Book"
            onClick={handleBook}
            disabled={!canBook}
            title={
              isBooking
                ? 'Booking…'
                : canBook
                  ? 'Book'
                  : 'Cannot book — misses the delivery window'
            }
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className={cn('h-3.5 w-3.5', isBooking && 'animate-pulse')} />
          </button>
        </div>
      </div>
    </div>
  );
}
